/* eslint-disable @typescript-eslint/no-explicit-any */
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Asset } from '@wsphere/assets/domain';
import { AssetDetailsCardComponent, AssetService } from '@wsphere/assets/web';
import { Provisioning } from '@wsphere/warranties/domain';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Subject,
  switchMap,
  takeUntil,
  startWith,
  tap,
} from 'rxjs';
import { ProvisioningSessionService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-provision-asset',
  templateUrl: './provision-asset.component.html',
  standalone: false,
})
export class ProvisionAssetComponent implements OnInit, OnDestroy {
  constructor(
    private assetSvc: AssetService,
    private propertySvc: PropertyService
  ) {}

  @HostBinding('class') _class = 'pb-24 px-6';

  private sessionSvc = inject(ProvisioningSessionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private toastSvc = inject(ToastService);

  protected stepCompleted = false;
  /**
   * Selected asset has all currently-required property-set fields filled in.
   * `null` while the check is running, so "still checking" is not shown as "missing".
   */
  protected assetComplete: boolean | null = null;
  private completenessCheck = 0;
  /** Property sets don't change during this step, so fetch each one once. */
  private propertySets = new Map<string, Promise<PropertySet.Model>>();
  protected destroy$ = new Subject<void>();

  protected createNew = false;
  private assetsReload$ = new BehaviorSubject(0);

  isLargeScreen$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => !state.matches));

  protected session$ = this.sessionSvc.getCurrentSession().pipe(
    tap(session => {
      if (session?.assetDraft) {
        this.createNew = true;
        return;
      }
      if (session?.asset) {
        this.asset.patchValue(session.asset, { emitEvent: false });
        this.stepCompleted = true;
      }
    })
  );

  protected asset = new FormControl(null as Asset.Model | null);

  readonly staffView$ = this.route.data.pipe(map(data => data['staffView'] as boolean));

  assets$ = combineLatest([this.session$, this.assetsReload$]).pipe(
    distinctUntilChanged(
      ([prevSession, prevReload], [currSession, currReload]) =>
        prevReload === currReload &&
        prevSession?.customerId === currSession?.customerId &&
        prevSession?.policy?.propertySetId === currSession?.policy?.propertySetId
    ),
    switchMap(async ([session]) => {
      const customerId = session?.customerId;
      const propertySetId = session?.policy?.propertySetId;
      return customerId
        ? (await this.assetSvc.getCustomerAssets(customerId, propertySetId)).filter(asset => asset.submissions?.length)
        : [];
    }),
    // Keep the selected asset in sync with the latest list, so its values aren't stale after an edit.
    tap(assets => {
      const selected = this.asset.value;
      const latest = selected && assets.find(asset => asset.id === selected.id);
      if (latest && latest !== selected) this.asset.setValue(latest, { emitEvent: false });
    })
  );

  reloadAssets() {
    AssetDetailsCardComponent.clearSubmissionCache(this.asset.value?.id ?? undefined);
    this.assetsReload$.next(this.assetsReload$.value + 1);
    void firstValueFrom(this.session$).then(session => this.refreshAssetCompleteness(this.asset.value, session, true));
  }

  private navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }

  previousPage(staffView: boolean) {
    if (staffView) this.navigateTo('/provisioning/policy');
    else this.navigateTo('/checkout/policy');
  }

  async nextPage(session: Provisioning.ProvisioningSession | null, staffView: boolean) {
    const asset = this.asset.value?.id;
    if (!asset) {
      this.toastSvc.show({
        type: 'error',
        title: 'Invalid asset selected',
        text: `Sorry, looks like something went wrong with your asset selection. Please try again.`,
      });
      return;
    }

    const complete = await this.refreshAssetCompleteness(this.asset.value, session, true);
    // The selection changed while this check ran, so its result is for the previous asset: don't save or navigate with it.
    if (this.asset.value?.id !== asset) return;
    if (!complete) {
      this.toastSvc.show({
        type: 'warning',
        title: 'Required asset details missing',
        text: 'Please edit the selected asset and fill in all required fields before continuing.',
      });
      return;
    }

    if (this.stepCompleted && session && asset) {
      if ((session.assetId && asset === session.assetId) || (!session.assetId && asset)) {
        await this.sessionSvc.updateSession({
          sessionId: session.id,
          data: { assetInfo: { id: asset } },
        });
      } else {
        await this.sessionSvc.updateSession({
          sessionId: session.id,
          data: {
            assetInfo: { id: asset },
            addonIds: [],
            planId: null as any,
            paymentSchedule: null as any,
          },
        });
      }
      if (staffView) this.navigateTo('/provisioning/plan');
      else this.navigateTo('/checkout/plan');
    }
  }

  async newAsset() {
    this.createNew = true;
  }

  ngOnInit(): void {
    // startWith: an asset restored from the session is patched without emitting, so check it on load too.
    combineLatest([this.asset.valueChanges.pipe(startWith(null)), this.session$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([, session]) => {
        const asset = this.asset.value;
        this.stepCompleted = !!asset;
        void this.refreshAssetCompleteness(asset, session);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * @param fresh Fetch the latest values from the server instead of using the ones the asset list returned.
   * Used as the final guard on Continue and after an asset is edited.
   */
  private async refreshAssetCompleteness(
    asset: Asset.Model | null,
    session: Provisioning.ProvisioningSession | null,
    fresh = false
  ): Promise<boolean> {
    // Only the latest check may set the result, so a slow check for a previous selection can't overwrite it.
    const check = ++this.completenessCheck;
    const settle = (complete: boolean) => {
      if (check === this.completenessCheck) this.assetComplete = complete;
      return complete;
    };

    if (!asset) return settle(false);

    const propertySetId = session?.policy?.propertySetId ?? asset.propertySetId;
    if (!propertySetId) return settle(false);

    this.assetComplete = null;

    try {
      const [propertySet, values] = await Promise.all([
        this.getPropertySet(propertySetId),
        !fresh && asset.values
          ? { value: asset.values, dimensionValues: asset.dimensionValues }
          : session?.id
            ? this.assetSvc.getAssetValuesForSales(asset.id, session.id)
            : this.assetSvc.getAssetValues(asset.id),
      ]);

      const missing = PropertySet.getMissingRequiredFields(
        propertySet,
        (values?.value ?? {}) as Record<string, unknown>,
        (values?.dimensionValues ?? {}) as Record<string, unknown>
      );
      return settle(missing.length === 0);
    } catch {
      // Fail closed — do not allow Continue if we cannot verify completeness.
      return settle(false);
    }
  }

  private getPropertySet(propertySetId: string): Promise<PropertySet.Model> {
    let request = this.propertySets.get(propertySetId);
    if (!request) {
      request = this.propertySvc.retrievePropertySet(propertySetId).catch(err => {
        this.propertySets.delete(propertySetId);
        throw err;
      });
      this.propertySets.set(propertySetId, request);
    }
    return request;
  }
}
